import {PayerType} from "../../../GraphQL/generated/graphql";
import {normalizeEnumKey} from "../../../i18n/labelHelpers";
import {filterActivityOptionsForFeeType} from "../../publicRegistration/publicRegistrationModel";
import type {StatusTone} from "../../../Components/ui/StatusTag";
import {EMPTY_OPERATIONS_VALUE} from "../participantOperationsHelpers";
import type {
    ActivityAnswerInput,
    ActivityFormValues,
    EventActivityAnswerValues,
    EventActivityOption,
    EventActivityQuestionTypeName,
    ParticipantPayerOption,
    ParticipantRegistration,
    RegistrationPayerInput
} from "./participantDetailTypes";

const getPrimaryRegistrationScore = (registration: ParticipantRegistration): number => {
    let score = 0;

    if (registration.currentParticipationOfferId) {
        score += 1000;
    }

    if (registration.currentParticipationOfferNumber?.trim()) {
        score += 500;
    }

    if (registration.currentAccommodationOfferNumber?.trim()) {
        score += 250;
    }

    if ((registration.selectedEventOptionIds?.length ?? 0) > 0) {
        score += 125;
    }

    if (registration.isEmailVerified) {
        score += 25;
    }

    if (registration.createdAt?.trim()) {
        score += 10;
    }

    return score;
};

// The registration the page edits: the one with the most operational progress, newest first on ties.
export const pickPrimaryRegistration = (registrations: ParticipantRegistration[]) => [...registrations]
    .sort((left, right) => {
        const scoreDifference = getPrimaryRegistrationScore(right) - getPrimaryRegistrationScore(left);

        return scoreDifference !== 0 ? scoreDifference : right.id - left.id;
    })[0];

export const normalizeEventActivityQuestionType = (
    value: string | number | undefined | null
): EventActivityQuestionTypeName => {
    if (typeof value === "number") {
        return value === 1
            ? "SingleChoice"
            : value === 2
                ? "MultiChoice"
                : value === 3
                    ? "Text"
                    : "YesNo";
    }

    const normalized = String(value ?? "")
        .replace(/[_\s-]+/g, "")
        .toLowerCase();

    if (normalized === "singlechoice") {
        return "SingleChoice";
    }

    if (normalized === "multichoice") {
        return "MultiChoice";
    }

    if (normalized === "text") {
        return "Text";
    }

    return "YesNo";
};

const getActiveActivityChoiceIds = (option: EventActivityOption): Set<number> =>
    new Set((option.choices ?? [])
        .filter(choice => choice?.isActive !== false)
        .map(choice => Number(choice.id))
        .filter(choiceId => Number.isFinite(choiceId) && choiceId > 0));

const normalizeActivityChoiceIds = (
    choiceIds: unknown,
    allowedChoiceIds: Set<number>
): number[] => {
    const rawChoiceIds = Array.isArray(choiceIds) ? choiceIds : [];

    return Array.from(new Set(rawChoiceIds
        .map(Number)
        .filter(choiceId => allowedChoiceIds.has(choiceId))))
        .sort((left, right) => left - right);
};

export const toPositiveIds = (values: unknown): number[] => (Array.isArray(values) ? values : [])
    .map(value => Number(value))
    .filter(value => Number.isFinite(value) && value > 0)
    .sort((left, right) => left - right);

export const hydrateActivityAnswerValues = (
    registration: ParticipantRegistration | undefined
): EventActivityAnswerValues => (registration?.activityAnswers ?? [])
    .reduce<EventActivityAnswerValues>((values, answer) => {
        if (!answer || answer.eventOptionId <= 0) {
            return values;
        }

        const questionType = normalizeEventActivityQuestionType(answer.questionType);

        if (questionType === "SingleChoice") {
            values[answer.eventOptionId] = answer.selectedChoiceIds?.[0];
        } else if (questionType === "MultiChoice") {
            values[answer.eventOptionId] = [...(answer.selectedChoiceIds ?? [])].sort((left, right) => left - right);
        } else if (questionType === "Text") {
            values[answer.eventOptionId] = answer.textValue?.trim() || answer.value?.trim() || "";
        }

        return values;
    }, {});

export const buildActivityFormValues = (registration: ParticipantRegistration | undefined): ActivityFormValues => ({
    yesNoOptionIds: toPositiveIds(registration?.selectedEventOptionIds),
    answers: Object.fromEntries(Object.entries(hydrateActivityAnswerValues(registration))) as ActivityFormValues["answers"]
});

export const buildActivityAnswerInputs = (
    activityOptions: EventActivityOption[],
    selectedYesNoOptionIds: number[],
    answerValues: EventActivityAnswerValues
): ActivityAnswerInput[] => {
    const selectedYesNoIds = new Set(selectedYesNoOptionIds);

    return activityOptions.reduce<ActivityAnswerInput[]>((answers, option) => {
        const questionType = normalizeEventActivityQuestionType(option.questionType);

        if (questionType === "YesNo") {
            if (selectedYesNoIds.has(option.id)) {
                answers.push({
                    eventOptionId: option.id,
                    isSelected: true,
                    selectedChoiceIds: []
                });
            }

            return answers;
        }

        if (questionType === "SingleChoice") {
            const choiceId = Number(answerValues[option.id]);
            if (getActiveActivityChoiceIds(option).has(choiceId)) {
                answers.push({
                    eventOptionId: option.id,
                    isSelected: true,
                    selectedChoiceIds: [choiceId]
                });
            }

            return answers;
        }

        if (questionType === "MultiChoice") {
            const selectedChoiceIds = normalizeActivityChoiceIds(answerValues[option.id], getActiveActivityChoiceIds(option));
            if (selectedChoiceIds.length > 0) {
                answers.push({
                    eventOptionId: option.id,
                    isSelected: true,
                    selectedChoiceIds
                });
            }

            return answers;
        }

        const rawTextValue = answerValues[option.id];
        const textValue = typeof rawTextValue === "string"
            ? rawTextValue.trim()
            : "";
        if (textValue) {
            answers.push({
                eventOptionId: option.id,
                isSelected: true,
                textValue,
                selectedChoiceIds: []
            });
        }

        return answers;
    }, []);
};

export const displayValue = (value?: string | number | null) => {
    if (typeof value === "number") {
        return String(value);
    }

    return value?.trim() || EMPTY_OPERATIONS_VALUE;
};

export const isPresent = (value?: string | number | null) => {
    if (typeof value === "number") {
        return Number.isFinite(value);
    }

    return !!value?.trim();
};

export const isLikelyValidEmail = (value?: string | null) => {
    const email = value?.trim() ?? "";
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const toPositiveInt = (value: unknown): number | undefined => {
    const numericValue = typeof value === "number"
        ? value
        : Number.parseInt(String(value ?? ""), 10);

    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : undefined;
};

export const buildParticipantFullName = (
    firstName?: string | null,
    lastName?: string | null,
    fallbackName?: string
) => `${firstName?.trim() ?? ""} ${lastName?.trim() ?? ""}`.trim() || fallbackName || "";

export const isQrInvitable = (status?: string | null) =>
    ["PRIJAVLJEN", "ODBIJEN", "POZVAN"].includes(normalizeEnumKey(status));

export const isRegistrationRejectable = (status?: string | null) =>
    ["PRIJAVLJEN", "POZVAN"].includes(normalizeEnumKey(status));

export const emailDeliveryTone = (status?: string | null): StatusTone => {
    const normalizedStatus = normalizeEnumKey(status);

    return normalizedStatus === "FAILED"
        ? "error"
        : normalizedStatus === "SENT"
            ? "success"
            : "info";
};

export const toPayerTypeInput = (value?: string | null): PayerType => {
    const normalized = value?.trim().toUpperCase();
    return Object.values(PayerType).find(type => type === normalized) ?? PayerType.Individual;
};

export const payerFormValuesFromPayer = (payer: ParticipantPayerOption): Omit<RegistrationPayerInput, "payerId"> => ({
    payerType: toPayerTypeInput(payer.type),
    payerName: payer.name?.trim() || "",
    payerEmail: payer.email?.trim() || "",
    payerPhone: payer.phone?.trim() || "",
    payerAddress: payer.address?.trim() || "",
    payerCity: payer.city?.trim() || "",
    payerCountry: payer.country?.trim() || "",
    payerTaxId: payer.taxId?.trim() || "",
    payerContactPerson: payer.contactPerson?.trim() || ""
});

// Activities shown and saved for a registration follow the public form: only those allowed for the
// registration's fee type, and dependents only while their parent answer matches. Hiding an option also
// drops its selection, which can hide further dependents, so this repeats until nothing changes.
export const resolveVisibleActivitySelection = (
    options: EventActivityOption[],
    feeTypeId: number | null | undefined,
    selectedOptionIds: number[]
): {visibleOptions: EventActivityOption[]; selectedOptionIds: number[]} => {
    let selected = selectedOptionIds;
    for (;;) {
        const visibleOptions = filterActivityOptionsForFeeType(options, feeTypeId, selected);
        const visibleIds = new Set(visibleOptions.map(option => option.id));
        const nextSelected = selected.filter(optionId => visibleIds.has(optionId));
        if (nextSelected.length === selected.length) {
            return {visibleOptions, selectedOptionIds: nextSelected};
        }

        selected = nextSelected;
    }
};
